import {IBaseDataRequest, TBankAccountSource} from '@common/types';

export type TTaskCenterPeriod = 'week' | 'month' | 'halfyear' | 'year';
export type TTaskCenterScope = 'all' | 'assigned' | 'created';

// separate overdue filter for task list
export type TTaskCenterStatus = 'overdue' | null;

export interface ITaskCenterType {
  id: number;
  name: string;
  title: string;
  icon: string;
  description: string;
  amount: number;
}

export const CLIENT_REQUEST_TASK_TYPE_ID = 7;

export const CLIENT_REQUEST_TASK_TYPE: ITaskCenterType = {
  id: CLIENT_REQUEST_TASK_TYPE_ID,
  name: 'Client Requests',
  title: 'Client Requests',
  icon: '',
  description: '',
  amount: 0,
};

export interface IAttachment {
  id: number | string;
  name: string;
  size: number;
  contentType: string;
}

export interface ITaskCenterTaskMessage {
  id: number;
  taskId: number;
  body: string;
  recipients?: ITaskCenterTaskMessageRecipient[];
  authorId: number;
  authorName: string;
  isAuthor: boolean;
  staffAuthored?: boolean;
  createdAt: string;
  updatedAt: string | null;
  attachments: IAttachment[];
  taskState?: number;
}

export interface ITaskCenterTaskMessageRecipient {
  id: number;
  name: string;
}

export interface ITaskCenterViewCounters {
  all: number;
  assigned: number;
  created: number;
}

export interface ITaskCenterStatusCounter {
  overdue: number;
  active: number;
  clarification: number;
  clarified: number;
  checking: number;
}

export interface ITaskCenterTaskCounters {
  open: number;
  overdue: number;
  clarified: number;
  completed: number;
}

// 0 - draft, 1 - sent, 2 - clarification, 3 - clarified, 4 - checking, 5 - completed
export enum TaskStatus {
  Draft = 0,
  Sent = 1,
  Clarification = 2,
  Clarified = 3,
  Checking = 4,
  Completed = 5,
}
export type TTaskStatus = TaskStatus;

// 0 - low, 1 - mid, 2 - high
export enum TaskPriority {
  Low,
  Mid,
  High,
}
export type TTaskPriority = TaskPriority;

// 0 - email, 1 - sms, 2 - both
export enum TaskNotifyVia {
  Email,
  Sms,
  Both,
}
export type TTaskNotifyVia = TaskNotifyVia;

export interface ITaskCenterTaskSettings {
  clientId?: number
  banks?: ITaskCenterSubjectInstance[];
  connections?: ITaskCenterSubjectInstance[];
  transactions?: ITaskCenterTransactionInstance[];
  [key: string]: unknown;
}

export interface ITaskCenterSubjectInstance {
  id: string;
  name: string;
  imageUrl: string;
  source?: TBankAccountSource;
  done?: boolean;
}

export interface ITaskCenterTransactionInstance {
  id: number;
  description: string;
  amount: number;
  date: string;
  accountName: string;
  accountId: number;
  done?: boolean;
  userComment?: string;
  processing?: boolean;
}

// one status filter in task list: SENT/CLARIFICATION/CLARIFIED/COMPLETED, null for "all"
export type TTaskCenterTaskStatus =
  | TaskStatus.Sent
  | TaskStatus.Clarification
  | TaskStatus.Clarified
  | TaskStatus.Checking
  | TaskStatus.Completed
  | null;

export type TTaskRecurrence = 'day' | 'week' | 'month' | 'quarter' | 'half-year' | 'year';

export const TASK_RECURRENCE_OPTIONS: {value: TTaskRecurrence; label: string}[] = [
  {value: 'day', label: 'Day'},
  {value: 'week', label: 'Week'},
  {value: 'month', label: 'Month'},
  {value: 'quarter', label: 'Quarter'},
  {value: 'half-year', label: 'Half-Year'},
  {value: 'year', label: 'Year'},
];

export const TASK_RECURRENCE_LABELS: Record<TTaskRecurrence, string> =
  TASK_RECURRENCE_OPTIONS.reduce((acc, {value, label}) => ({...acc, [value]: label}), {} as Record<TTaskRecurrence, string>);

export interface ITaskCenterTask {
  id: number;
  typeId: number;
  description: string;
  ownerId: number;
  authorId: number;
  assigneeId: number;
  status: TTaskStatus;
  // might be null
  priority: TTaskPriority | null;
  // might be null
  notifyVia: TTaskNotifyVia | null;
  // might be null - recurrence interval for a recurrent task
  recurrence: TTaskRecurrence | null;
  // might be null - id of the original task this recurrent task was spawned from
  originalTaskId: number | null;
  dueDate: string;
  settings: string;
  // defaults false
  viewed: boolean;
  // defaults false
  archived: boolean;
  sentAt: string;
  completedAt: string;
  createdAt: string;
  updatedAt: string;
}

export type TTaskCenterUserType = 'client' | 'employee' | 'owner' | 'admin';

export const TASK_CENTER_USER_TYPE_META: Record<TTaskCenterUserType, {label: string; groupLabel: string; order: number}> = {
  client: {label: 'Client', groupLabel: 'Clients', order: 0},
  owner: {label: 'Owner', groupLabel: 'Owner', order: 1},
  admin: {label: 'Admin', groupLabel: 'Admin', order: 2},
  employee: {label: 'Team', groupLabel: 'Team', order: 3},
};

export const TASK_CENTER_USER_TYPE_ORDER: TTaskCenterUserType[] = ['client', 'owner', 'employee', 'admin'];

export interface ITaskCenterUser {
  id: number;
  name: string;
  type: TTaskCenterUserType;
}

export interface ITaskCenterTaskListRequest extends IBaseDataRequest {
  scope?: TTaskCenterScope;
  overdueOnly?: boolean;
  status?: TTaskCenterTaskStatus;
  typeId?: number;
  authorId?: number;
  assigneeId?: number;
  createdFrom?: string;
  createdTo?: string;
  dueFrom?: string;
  dueTo?: string;
}

export interface ITaskCenterTaskListItem extends Omit<ITaskCenterTask, 'settings'> {
  typeName: string;
  authorName: string;
  assigneeName: string;
  isAuthor: boolean;
  staffAuthored?: boolean;
  isAssignee: boolean;
  settings: ITaskCenterTaskSettings;
  messages: ITaskCenterTaskMessage[];
  attachmentsList: IAttachment[];
}


export interface ITaskCenterTaskListItemBackend extends Omit<ITaskCenterTaskListItem, 'settings'> {
  settings: string;
}

export interface ITaskCenterMessageListItem {
  taskId: number;
  subjectTitle: string;
  description: string;
  lastMessage: ITaskCenterTaskMessage | null;
}

export interface ITaskCenterRecurringTask {
  id: number;
  originalTaskId: number;
  description: string;
  typeName: string;
  recurrence: TTaskRecurrence;
  lastCreationDate: string;
}
